import React, { useEffect, useState } from "react";
import styled from "styled-components";
import HostHubActivity from "./Cards/HostHubActivity/HostHubActivity";
import Coa from "./Cards/Coa";
import FinancialForm from "./Cards/FinancialForm";
import EventSchedule from "./Cards/EventSchedule";
import Homepage from "./Cards/Homepage";
import OnlineShopping from "./Cards/OnlineShopping";
import EWallet from "./Cards/EWallet";
import {
	OrganizationData,
	FairInfoData,
	ChairpersonData,
	FinancialFormData,
	CoChairData,
	EWalletData,
	OnlineShoppingData,
	HomepageData,
	AnalyticsData,
} from "hooks/ConsultantDashboard/useConsultantDashboardData";
import {
	convertDateToYMD,
	convertYMDStringToDate,
	convertYMDToMDY,
} from "utils/DateConversion";

interface Props {
	organizationData: OrganizationData;
	fairInfoData: FairInfoData;
	chairpersonData: ChairpersonData;
	financialFormData: FinancialFormData;
	coChairData: CoChairData;
	eWalletData: EWalletData;
	onlineShoppingData: OnlineShoppingData;
	homepageData: HomepageData;
	analyticsData: AnalyticsData;
	fairId: string;
}

const ConsultantDashboardPageContent = ({
	organizationData,
	fairInfoData,
	chairpersonData,
	financialFormData,
	coChairData,
	eWalletData,
	onlineShoppingData,
	homepageData,
	analyticsData,
	fairId,
}: Props) => {
	const [afterOneYear, setAfterOneYear] = useState(true);

	useEffect(() => {
		// Add one year to end date
		const oneYearAfterFairEnd = convertYMDStringToDate(
			fairInfoData.endDate
		);
		oneYearAfterFairEnd.setFullYear(oneYearAfterFairEnd.getFullYear() + 1);
		// convert Date back to YMD string for comparison without timestamp
		const oneYearAfterFairEndString = convertDateToYMD(oneYearAfterFairEnd);
		// get current date in YMD string
		const currentDate = convertDateToYMD(new Date());
		// if one year has passed since fairEndDate, set afterOneYear to true
		setAfterOneYear(currentDate >= oneYearAfterFairEndString);
	}, [fairInfoData.endDate]);

	return (
		<Grid>
			<FullWidth>
				<Heading>
					<HeadingTitle>{organizationData.name}</HeadingTitle>
					<HeadingFairInfo>
						<HeadingFairInfoText>
							Account #: {organizationData.bookfairAccountId}
						</HeadingFairInfoText>
						<HeadingFairInfoText>
							Fair ID: {fairId}
						</HeadingFairInfoText>
						<HeadingFairInfoText>
							Fair Date: {convertYMDToMDY(fairInfoData.startDate)}{" "}
							- {convertYMDToMDY(fairInfoData.endDate)}
						</HeadingFairInfoText>
					</HeadingFairInfo>
				</Heading>
			</FullWidth>
			<LeftColumn>
				<HostHubActivity
					chairpersonData={chairpersonData}
					coChairData={coChairData}
					afterOneYear={afterOneYear}
					logins={analyticsData.logins}
				/>
				<EWallet ewalletData={eWalletData} />
			</LeftColumn>
			<RightColumn>
				<Coa coaStatus={fairInfoData.coaStatus} />
				<FinancialForm financialFormData={financialFormData} />
			</RightColumn>
			<LeftColumn>
				<OnlineShopping onlineShoppingData={onlineShoppingData} />
				{homepageData !== null && (
					<Homepage
						afterOneYear={afterOneYear}
						homepageData={homepageData}
						visits={analyticsData.homePageVisits}
					/>
				)}
				{!afterOneYear && (
					<EventSchedule
						eventSchedule={
							homepageData ? homepageData.events : null
						}
					/>
				)}
			</LeftColumn>
		</Grid>
	);
};

export const Heading = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	width: 100%;
	justify-content: space-between;
	margin: 74px 0 50px 0;
	row-gap: 30px;
	@media (max-width: 685px) {
		flex-direction: column;
		margin: 61px 0px 37px 0px;
	}
	@media (max-width: 519px) {
		margin: 54px 0px 30px 0px;
	}
`;

export const HeadingTitle = styled.p`
	font-weight: 500;
	font-size: 39px;
	line-height: 42px;
	color: #333333;
	@media (max-width: 720px) {
		font-size: 36px;
		line-height: 39px;
	}
`;
const HeadingFairInfo = styled.div`
	display: flex;
	flex-direction: column;
	@media (max-width: 685px) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const HeadingFairInfoText = styled.p`
	font-style: normal;
	font-weight: 500;
	font-size: 13px;
	line-height: 15px;
	text-align: right;
	color: #333333;
`;

const FullWidth = styled.div`
	grid-column-start: 1;
	grid-column-end: 24;
`;

const LeftColumn = styled.div`
	grid-column-start: 1;
	grid-column-end: 16;
	@media (max-width: 720px) {
		grid-column-start: 1;
		grid-column-end: 24;
	}
`;
const RightColumn = styled.div`
	grid-column-start: 17;
	grid-column-end: 24;
	@media (max-width: 720px) {
		display: flex;
		flex-direction: row;
		column-gap: 27px;
		grid-column-start: 1;
		grid-column-end: 24;
	}
	@media (max-width: 519px) {
		display: block;
	}
`;

export const Grid = styled.div`
	display: grid;
	margin: 0 auto 24px;
	grid-template-columns: repeat(23, 1fr);
	width: -webkit-fill-available;
	width: -moz-available;
	max-width: 1240px;

	/* IE friendly.....*/
	display: -ms-grid;
	-ms-grid-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr
		1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

	@media (max-width: 1599px) {
		margin: 0px 180px 24px;
	}

	@media (max-width: 1279px) {
		margin: 0px 140px 24px;
	}

	@media (max-width: 959px) {
		margin: 0px 40px 24px;
	}

	@media (max-width: 519px) {
		margin: 0px 20px 24px;
	}
`;

export default ConsultantDashboardPageContent;
